/**
 * Frontend currency utilities for the experience checkout flow.
 *
 * Supported currencies match the backend StripeFXService list.
 * JPY is the only zero-decimal currency in this set.
 */

export const SUPPORTED_CURRENCIES = [
  'USD', 'EUR', 'GBP', 'AED', 'INR',
  'JPY', 'CAD', 'AUD', 'CHF', 'SGD', 'HKD',
];

const ZERO_DECIMAL_CURRENCIES = ['JPY'];

const CURRENCY_SYMBOLS = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  AED: 'AED ',
  INR: '₹',
  JPY: '¥',
  CAD: 'CA$',
  AUD: 'A$',
  CHF: 'CHF ',
  SGD: 'S$',
  HKD: 'HK$',
};

/**
 * Normalise a currency code to UPPERCASE and trim whitespace.
 */
export function normalizeCurrency(code) {
  if (!code || typeof code !== 'string') return '';
  return code.trim().toUpperCase();
}

/**
 * Returns true if the currency uses zero-decimal units (e.g. JPY).
 */
export function isZeroDecimal(currencyCode) {
  return ZERO_DECIMAL_CURRENCIES.includes(normalizeCurrency(currencyCode));
}

/**
 * Returns true if the currency is in the supported list.
 */
export function isSupported(currencyCode) {
  return SUPPORTED_CURRENCIES.includes(normalizeCurrency(currencyCode));
}

/**
 * Return a display symbol for a currency.
 * Falls back to '{CODE} ' for unknown currencies.
 */
export function getSymbol(currencyCode) {
  const c = normalizeCurrency(currencyCode);
  return CURRENCY_SYMBOLS[c] || `${c} `;
}

/**
 * Format a major-unit amount as a human-readable string.
 *
 * Uses Intl.NumberFormat so JPY automatically omits decimal places.
 *
 * @param {number} amount  - Major-unit amount (e.g. 1500 for ¥1,500 or 12.50 for $12.50)
 * @param {string} currencyCode
 * @returns {string}  e.g. "¥1,500"  "$12.50"  "AED 25.00"
 */
export function formatPrice(amount, currencyCode) {
  const c = normalizeCurrency(currencyCode);
  if (!c) return String(amount);
  try {
    const decimals = isZeroDecimal(c) ? 0 : 2;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: c,
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(Number(amount) || 0);
  } catch (_) {
    // Graceful fallback for unsupported Intl currency codes
    const sym = getSymbol(c);
    const decimals = isZeroDecimal(c) ? 0 : 2;
    return `${sym}${Number(amount || 0).toFixed(decimals)}`;
  }
}

/**
 * Format amount as a digit-only string with appropriate decimal places.
 * Useful when you need to display a raw number alongside a separate symbol.
 */
export function formatAmount(amount, currencyCode) {
  const c = normalizeCurrency(currencyCode);
  const decimals = isZeroDecimal(c) ? 0 : 2;
  return Number(amount || 0).toFixed(decimals);
}
